import React, { useState, useEffect } from 'react';
import {
    FaFileAlt,
    FaShieldAlt,
    FaUniversity,
    FaSearch,
    FaToggleOn,
    FaToggleOff,
    FaMoneyBillWave,
    FaSave,
    FaExclamationCircle,
    FaStar
} from 'react-icons/fa';
import * as adminService from '../services/adminService';
import Toast from '../components/Toast';

const AdminYIPaymentSettings = () => {
    const [settings, setSettings] = useState({
        yi_article_payment_enabled: 'false',
        yi_article_price: '0'
    });
    const [universities, setUniversities] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [savingSetting, setSavingSetting] = useState(null);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await adminService.getYIArticlePaymentSettings();
            if (res.success) {
                setSettings(prev => ({ ...prev, ...res.data.settings }));
                setUniversities(res.data.universities || []);
            }
        } catch (error) {
            showToast(error.message || 'Failed to load YI payment settings', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleSettingUpdate = async (key, value) => {
        setSavingSetting(key);
        try {
            const response = await adminService.updateYIArticlePaymentSetting(key, value);
            if (response.success) {
                setSettings(prev => ({ ...prev, [key]: String(value) }));
                showToast('Setting updated successfully');

                // Refetch universities after global toggle to reflect new states
                if (key === 'yi_article_payment_enabled') {
                    const res = await adminService.getYIArticlePaymentSettings();
                    if (res.success) {
                        setUniversities(res.data.universities || []);
                    }
                }
            }
        } catch (error) {
            showToast(error.message || 'Update failed', 'error');
        } finally {
            setSavingSetting(null);
        }
    };

    const handleUniToggle = async (uniId, currentStatus) => {
        try {
            const response = await adminService.toggleUniversityFreeYIArticle(uniId, !currentStatus);
            if (response.success) {
                setUniversities(prev =>
                    prev.map(u =>
                        u.id === uniId ? { ...u, is_free_yi_article: !currentStatus } : u
                    )
                );
                showToast('University YI article payment status updated');
            }
        } catch (error) {
            showToast(error.message || 'Toggle failed', 'error');
        }
    };

    const filteredUnis = universities.filter(u =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.state?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const darkGradient = {
        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)'
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    const isPaymentEnabled = settings.yi_article_payment_enabled === 'true';

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Header */}
            <div className="py-12 px-4 shadow-lg text-white" style={darkGradient}>
                <div className="max-w-7xl mx-auto flex items-center gap-4">
                    <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
                        <FaFileAlt className="text-3xl text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-extrabold">YI Article Payment Settings</h1>
                        <p className="text-purple-100 opacity-80">
                            Manage Young India article submission fees and university exemptions
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Left Column: Global Settings */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                        <div className="p-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex items-center gap-3">
                            <FaShieldAlt />
                            <h2 className="font-bold">YI Article Payment Control</h2>
                        </div>
                        <div className="p-8 space-y-8">

                            {/* Global Toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div>
                                    <p className="font-bold text-gray-900">Enable Article Payment</p>
                                    <p className="text-xs text-gray-500">Require fee for YI article submission</p>
                                </div>
                                <button
                                    onClick={() =>
                                        handleSettingUpdate(
                                            'yi_article_payment_enabled',
                                            isPaymentEnabled ? 'false' : 'true'
                                        )
                                    }
                                    className={`text-4xl transition-colors ${isPaymentEnabled ? 'text-green-500' : 'text-gray-300'}`}
                                    disabled={savingSetting === 'yi_article_payment_enabled'}
                                    title={isPaymentEnabled ? 'Click to disable payment' : 'Click to enable payment'}
                                >
                                    {isPaymentEnabled ? <FaToggleOn /> : <FaToggleOff />}
                                </button>
                            </div>

                            {/* Price Input */}
                            <div className="space-y-3">
                                <label className="block font-bold text-gray-700 flex items-center gap-2">
                                    <FaMoneyBillWave className="text-green-500" />
                                    Article Submission Fee (INR)
                                </label>
                                <div className="flex gap-2">
                                    <div className="relative flex-grow">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-gray-400">₹</span>
                                        <input
                                            type="number"
                                            value={settings.yi_article_price}
                                            onChange={(e) =>
                                                setSettings(prev => ({ ...prev, yi_article_price: e.target.value }))
                                            }
                                            className="w-full pl-8 pr-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-purple-500 outline-none font-bold"
                                            min="0"
                                        />
                                    </div>
                                    <button
                                        onClick={() =>
                                            handleSettingUpdate('yi_article_price', settings.yi_article_price)
                                        }
                                        disabled={savingSetting === 'yi_article_price'}
                                        className="px-6 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all flex items-center gap-2 shadow-lg shadow-purple-200"
                                    >
                                        <FaSave />
                                        Save
                                    </button>
                                </div>
                            </div>

                            {/* Info Box */}
                            <div className="p-4 bg-purple-50 rounded-2xl border border-purple-100 flex gap-3">
                                <FaExclamationCircle className="text-purple-500 mt-1 flex-shrink-0" />
                                <p className="text-xs text-purple-700 leading-relaxed">
                                    When enabled, students must pay this fee to submit Young India articles.
                                    Turning the global toggle <strong>ON</strong> enables payment for all universities.
                                    Turning it <strong>OFF</strong> makes all universities free.
                                    You can then exempt individual universities using the list on the right.
                                </p>
                            </div>

                            {/* Status Pill */}
                            <div className={`flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm ${isPaymentEnabled
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-gray-50 text-gray-500 border border-gray-200'
                                }`}>
                                <FaStar className={isPaymentEnabled ? 'text-green-500' : 'text-gray-400'} />
                                {isPaymentEnabled
                                    ? `Payment Active — ₹${Number(settings.yi_article_price).toLocaleString()} per article`
                                    : 'Payment Disabled — All submissions free'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: University List */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 flex flex-col h-[700px]">
                        <div className="p-6 bg-gradient-to-r from-violet-600 to-purple-600 text-white flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FaUniversity />
                                <div>
                                    <h2 className="font-bold">University Payment List</h2>
                                    <p className="text-xs text-white/70 mt-0.5">
                                        Toggle OFF a university to exempt it from YI article payment
                                    </p>
                                </div>
                            </div>
                            <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold">
                                {universities.length} Universities
                            </span>
                        </div>

                        {/* Search */}
                        <div className="p-6 border-b border-gray-100">
                            <div className="relative">
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search university by name or state..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-purple-500 outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* Table */}
                        <div className="flex-grow overflow-y-auto p-0 scrollbar-thin">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase">University Name</th>
                                        <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase">State</th>
                                        <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase text-center">
                                            Payment Required
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredUnis.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="px-8 py-12 text-center text-gray-400 font-medium italic">
                                                {searchTerm
                                                    ? `No universities found matching "${searchTerm}"`
                                                    : 'No universities found'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredUnis.map(uni => (
                                            <tr key={uni.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-8 py-5">
                                                    <p className="font-bold text-gray-900">{uni.name}</p>
                                                    <p className="text-[10px] text-gray-400 font-mono tracking-tighter">ID: {uni.id}</p>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className="text-sm text-gray-600 font-medium">{uni.state || 'N/A'}</span>
                                                </td>
                                                <td className="px-8 py-5 text-center">
                                                    <button
                                                        onClick={() => handleUniToggle(uni.id, uni.is_free_yi_article)}
                                                        className={`text-4xl transition-all hover:scale-110 active:scale-95 ${!uni.is_free_yi_article
                                                                ? 'text-purple-600'
                                                                : 'text-gray-300'
                                                            }`}
                                                        title={
                                                            uni.is_free_yi_article
                                                                ? 'Payment exempt (free). Click to require payment.'
                                                                : 'Payment required. Click to exempt this university.'
                                                        }
                                                    >
                                                        {/* Toggle ON (purple) = payment required, Toggle OFF (gray) = free */}
                                                        {!uni.is_free_yi_article ? <FaToggleOn /> : <FaToggleOff />}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminYIPaymentSettings;
